/* ==========================================================================
   UNCLENCH neo engine — "powering down"
   - WebGL2 tension field: a stack of signal-lines displaced by fbm noise.
     One uniform (uTension 1→0) drives amplitude, jitter, speed and the
     ember→dusk color temperature. The scroll position owns that uniform.
   - The same scalar is mirrored to CSS (--nx-t, --nx-soft, --nx-dwght) so
     the palette and the Fraunces SOFT/weight axes relax with the page.
   - HUD: fixed rail with live index (87→04), clock (18:47→23:00), sparkline.
   Everything degrades: no WebGL2 → static gradient; reduced motion → the
   calm end-state, statically; occluded rAF only pauses the field.
   ========================================================================== */

(function () {
  "use strict";

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var designMode = document.documentElement.classList.contains("shopify-design-mode");

  /* ------------------------------------------------------------------ */
  /* shared state                                                        */
  /* ------------------------------------------------------------------ */

  var tension = { target: 1, value: 1 };
  var pointer = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };

  function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  function smootherstep(t) {
    return t * t * t * (t * (t * 6 - 15) + 10);
  }

  /* ------------------------------------------------------------------ */
  /* field — WebGL2 fullscreen shader                                    */
  /* ------------------------------------------------------------------ */

  var FRAG = [
    "#version 300 es",
    "precision highp float;",
    "uniform vec2 uRes;",
    "uniform float uTime;",
    "uniform float uT;", // tension 1 hot -> 0 calm
    "uniform vec2 uPointer;",
    "uniform float uLines;",
    "out vec4 outColor;",

    "float hash(vec2 p){ p = fract(p * vec2(234.34, 435.345)); p += dot(p, p + 34.23); return fract(p.x * p.y); }",
    "float noise(vec2 p){ vec2 i = floor(p); vec2 f = fract(p); f = f * f * (3.0 - 2.0 * f);",
    "  float a = hash(i), b = hash(i + vec2(1.0, 0.0)), c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));",
    "  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y); }",
    "float fbm(vec2 p){ float v = 0.0; float a = 0.5;",
    "  for (int i = 0; i < 4; i++){ v += a * noise(p); p = p * 2.04 + 17.0; a *= 0.5; } return v; }",

    "void main(){",
    "  vec2 uv = gl_FragCoord.xy / uRes;",
    "  float aspect = uRes.x / uRes.y;",
    "  float t = uT;",
    "  float calm = 1.0 - t;",

    // line lattice: uv.y split into bands, each band carries one signal line
    "  float bands = uLines;",
    "  float band = uv.y * bands;",
    "  float speed = mix(0.09, 0.55, t);",
    "  float freq  = mix(1.2, 3.4, t);",
    "  float ampBase = mix(0.34, 1.55, t);",           // in band units; calm still breathes
    "  float jitter = t * t;",

    // pointer swell — a slow local lift near the cursor
    "  float pd = distance(vec2(uv.x * aspect, uv.y), vec2(uPointer.x * aspect, uPointer.y));",
    "  float swell = exp(-pd * pd * 9.0) * 0.55;",

    "  vec3 col = vec3(0.0);",
    "  for (int k = -2; k <= 2; k++){",
    "    float li = floor(band) + float(k);",
    "    if (li < 0.0 || li >= bands) continue;",
    "    float seed = li * 7.31;",
    // main displacement: smooth fbm swell + a high-frequency nervous tremor that dies with calm
    "    float n = fbm(vec2(uv.x * freq + seed, uTime * speed + seed));",
    "    float tremor = (noise(vec2(uv.x * 26.0 + seed, uTime * (2.2 + jitter * 3.0))) - 0.5) * jitter * 0.5;",
    "    float disp = (n - 0.5) * ampBase + tremor + swell * (0.35 + 0.65 * t) * sin(seed);",
    "    float lineY = (li + 0.5 + disp) / bands;",
    "    float d = abs(uv.y - lineY);",
    // glow core + halo; lines sharpen when tense, soften when calm
    "    float core = smoothstep(mix(0.0035, 0.0022, t), 0.0, d);",
    "    float halo = smoothstep(mix(0.05, 0.028, t), 0.0, d) * 0.16;",
    // hue: per-line variation, ember-red when hot, dusk-indigo when calm
    "    float hv = hash(vec2(li, 3.7));",
    "    vec3 hot  = mix(vec3(0.77, 0.14, 0.19), vec3(1.0, 0.42, 0.27), hv * 0.7);", // lacquer -> ember
    "    vec3 cool = mix(vec3(0.17, 0.16, 0.32), vec3(0.66, 0.61, 0.78), hv * 0.55);", // dusk -> lavender
    "    vec3 lineCol = mix(cool, hot, t);",
    "    float depth = 0.45 + 0.55 * hv;",
    "    col += lineCol * (core * 0.9 + halo) * depth * mix(0.5, 1.0, t * 0.7 + 0.3);",
    "  }",

    // ambient wash: hot ember top-right when tense, dusk pool bottom when calm
    "  vec3 washHot = vec3(0.20, 0.045, 0.05) * exp(-distance(uv, vec2(0.82, 0.85)) * 1.6);",
    "  vec3 washCool = vec3(0.075, 0.07, 0.16) * exp(-distance(uv, vec2(0.3, 0.12)) * 1.5);",
    "  col += mix(washCool, washHot, t) * 1.1;",

    // vignette + animated grain
    "  float vig = smoothstep(1.25, 0.35, distance(uv, vec2(0.5, 0.48)));",
    "  col *= mix(0.72, 1.0, vig);",
    "  float g = hash(gl_FragCoord.xy + fract(uTime) * 61.0) - 0.5;",
    "  col += g * mix(0.012, 0.05, t);",

    "  vec3 base = vec3(0.043, 0.039, 0.07);",
    "  outColor = vec4(base + col, 1.0);",
    "}"
  ].join("\n");

  var VERT = [
    "#version 300 es",
    "void main(){",
    "  vec2 p = vec2((gl_VertexID << 1 & 2) * 2 - 1, (gl_VertexID & 2) * 2 - 1);",
    "  gl_Position = vec4(p, 0.0, 1.0);",
    "}"
  ].join("\n");

  function createField() {
    var canvas = document.createElement("canvas");
    canvas.id = "nx-field";
    canvas.setAttribute("aria-hidden", "true");
    document.body.prepend(canvas);

    var gl = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: "high-performance"
    });

    if (!gl) {
      canvas.remove();
      var fb = document.createElement("div");
      fb.className = "nx-field-fallback";
      fb.setAttribute("aria-hidden", "true");
      document.body.prepend(fb);
      return null;
    }

    function compile(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        throw new Error("nx shader: " + gl.getShaderInfoLog(s));
      }
      return s;
    }

    var prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error("nx link: " + gl.getProgramInfoLog(prog));
    }
    gl.useProgram(prog);

    var u = {
      res: gl.getUniformLocation(prog, "uRes"),
      time: gl.getUniformLocation(prog, "uTime"),
      t: gl.getUniformLocation(prog, "uT"),
      pointer: gl.getUniformLocation(prog, "uPointer"),
      lines: gl.getUniformLocation(prog, "uLines")
    };

    var isMobile = window.matchMedia("(max-width: 749px)").matches;
    var scale = (Math.min(window.devicePixelRatio || 1, 1.5)) * (isMobile ? 0.5 : 0.62);

    function resize() {
      var w = Math.max(1, Math.floor(window.innerWidth * scale));
      var h = Math.max(1, Math.floor(window.innerHeight * scale));
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(u.res, w, h);
    }
    window.addEventListener("resize", resize, { passive: true });
    resize();

    gl.uniform1f(u.lines, isMobile ? 14 : 20);

    return {
      draw: function (timeSec) {
        gl.uniform1f(u.time, timeSec);
        gl.uniform1f(u.t, tension.value);
        gl.uniform2f(u.pointer, pointer.x, 1 - pointer.y);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      }
    };
  }

  /* ------------------------------------------------------------------ */
  /* HUD                                                                 */
  /* ------------------------------------------------------------------ */

  function createHud() {
    var hud = document.createElement("div");
    hud.id = "nx-hud";
    hud.setAttribute("aria-hidden", "true");
    hud.innerHTML =
      '<span class="nx-hud__label">Tension index</span>' +
      '<div class="nx-hud__track"><i class="nx-hud__fill"></i></div>' +
      '<div class="nx-hud__mid">' +
      '<canvas width="52" height="168"></canvas>' +
      '<span class="nx-hud__num">87</span>' +
      '<span class="nx-hud__clock">18:47</span>' +
      "</div>";
    document.body.prepend(hud);

    var bar = document.createElement("div");
    bar.id = "nx-hud-bar";
    bar.setAttribute("aria-hidden", "true");
    document.body.prepend(bar);

    var num = hud.querySelector(".nx-hud__num");
    var clock = hud.querySelector(".nx-hud__clock");
    var spark = hud.querySelector("canvas");
    var ctx = spark.getContext("2d");

    var lastNum = -1;
    var lastClock = "";

    return {
      update: function (timeSec) {
        var t = tension.value;
        var idx = Math.round(4 + t * 83);
        if (idx !== lastNum) {
          lastNum = idx;
          num.textContent = (idx < 10 ? "0" : "") + idx;
        }
        // 18:47 -> 23:00
        var mins = Math.round(1127 + (1 - t) * 253);
        var hh = Math.floor(mins / 60);
        var mm = mins % 60;
        var label = hh + ":" + (mm < 10 ? "0" : "") + mm;
        if (label !== lastClock) {
          lastClock = label;
          clock.textContent = label;
        }
        // sparkline: vertical trace, nervous when tense
        var w = spark.width, h = spark.height;
        ctx.clearRect(0, 0, w, h);
        ctx.beginPath();
        var amp = (2 + t * 16);
        for (var y = 0; y <= h; y += 3) {
          var n =
            Math.sin(y * 0.11 + timeSec * (0.8 + t * 4)) * 0.6 +
            Math.sin(y * 0.31 + timeSec * (1.7 + t * 7)) * 0.4 * t;
          var x = w / 2 + n * amp;
          y === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        // lacquer #c42430 -> dusk #575097, mixed by calm (CSS --nx-live equivalent)
        var calm = 1 - t;
        ctx.strokeStyle =
          "rgb(" +
          Math.round(196 + (87 - 196) * calm) + "," +
          Math.round(36 + (80 - 36) * calm) + "," +
          Math.round(48 + (151 - 48) * calm) + ")";
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    };
  }

  /* ------------------------------------------------------------------ */
  /* reveals — IntersectionObserver, CSS does the easing                 */
  /* ------------------------------------------------------------------ */

  function initReveals(root) {
    var els = (root || document).querySelectorAll("[data-nx-r]:not(.is-in)");
    if (!els.length) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { root: null, rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    );
    els.forEach(function (el, i) {
      var group = el.closest("[data-nx-stagger]");
      if (group) {
        var sibs = group.querySelectorAll("[data-nx-r]");
        var idx = Array.prototype.indexOf.call(sibs, el);
        el.style.transitionDelay = (idx * 70) + "ms";
      }
      io.observe(el);
    });
  }

  /* ------------------------------------------------------------------ */
  /* ritual rail — step counter follows the visible step                 */
  /* ------------------------------------------------------------------ */

  function initRitual() {
    var rail = document.querySelector(".nx-rit__rail");
    var steps = document.querySelectorAll(".nx-rit__step");
    if (!rail || !steps.length) return;
    var count = rail.querySelector("[data-nx-count]");
    var word = rail.querySelector("[data-nx-word]");
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          var i = Array.prototype.indexOf.call(steps, e.target) + 1;
          if (count) count.innerHTML = "<b>0" + i + "</b>&thinsp;/&thinsp;0" + steps.length;
          if (word && word.textContent !== e.target.dataset.nxStepWord) {
            word.style.opacity = 0;
            setTimeout(function () {
              word.textContent = e.target.dataset.nxStepWord || "";
              word.style.transition = "opacity .5s";
              word.style.opacity = 1;
            }, 160);
          }
        });
      },
      { threshold: 0.45 }
    );
    steps.forEach(function (s) { io.observe(s); });
  }

  /* ------------------------------------------------------------------ */
  /* GSAP choreography (hero intro, drifts, scrubs)                      */
  /* ------------------------------------------------------------------ */

  function scroller() {
    var pw = document.querySelector(".page-wrapper");
    if (pw) {
      var o = getComputedStyle(pw).overflowY;
      if (o === "auto" || o === "scroll") return pw;
    }
    return window;
  }

  function initGsap(sc) {
    if (!window.gsap || !window.ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);
    if (sc !== window) ScrollTrigger.defaults({ scroller: sc });

    // hero intro — lines rise, device settles
    var lines = document.querySelectorAll(".nx-hero__headline .nx-line > span");
    if (lines.length) {
      gsap.fromTo(
        lines,
        { yPercent: 112 },
        { yPercent: 0, duration: 1.25, ease: "expo.out", stagger: 0.09, delay: 0.15 }
      );
    }
    var intro = document.querySelectorAll(".nx-hero [data-nx-intro]");
    if (intro.length) {
      gsap.fromTo(
        intro,
        { opacity: 0, y: 18 },
        { opacity: 1, y: 0, duration: 1.0, ease: "power2.out", stagger: 0.1, delay: 0.7 }
      );
    }

    var device = document.querySelector(".nx-hero__device");
    if (device) {
      gsap.fromTo(
        device,
        { opacity: 0, y: 60, rotation: -12 },
        { opacity: 1, y: 0, rotation: -7, duration: 1.6, ease: "expo.out", delay: 0.35 }
      );
      // breathing — the only permanent loop, slow on purpose
      gsap.to(device, {
        y: "-=12",
        rotation: -5.4,
        duration: 3.6,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
        delay: 2
      });
      // pointer sway
      if (window.matchMedia("(pointer: fine)").matches) {
        var qx = gsap.quickTo(device, "x", { duration: 1.1, ease: "power3.out" });
        window.addEventListener(
          "pointermove",
          function (e) {
            qx((e.clientX / window.innerWidth - 0.5) * 18);
          },
          { passive: true }
        );
      }
    }

    // ghost clock parallax
    var clock = document.querySelector(".nx-hero__clock");
    if (clock) {
      gsap.to(clock, {
        yPercent: 34,
        opacity: 0.4,
        ease: "none",
        scrollTrigger: { trigger: ".nx-hero", start: "top top", end: "bottom top", scrub: true }
      });
    }

    // instrument still-life: settle the ken-burns scale
    var inst = document.querySelector(".nx-inst__stage img");
    if (inst) {
      gsap.fromTo(
        inst,
        { scale: 1.09 },
        {
          scale: 1,
          ease: "none",
          scrollTrigger: { trigger: ".nx-inst__stage", start: "top 90%", end: "bottom 20%", scrub: true }
        }
      );
    }
  }

  /* ------------------------------------------------------------------ */
  /* master scroll → tension                                             */
  /* ------------------------------------------------------------------ */

  function initTensionTracking(sc) {
    var el = sc === window ? document.documentElement : sc;

    function measure() {
      var top = sc === window ? (window.scrollY || document.documentElement.scrollTop) : sc.scrollTop;
      var max = el.scrollHeight - el.clientHeight;
      if (max <= 0) return 0;
      return clamp01(top / max);
    }

    function onScroll() {
      var p = measure();
      // reach full calm at 92% of the page
      tension.target = 1 - smootherstep(clamp01(p / 0.92));
      document.body.classList.toggle("nx-scrolled", p > 0.005);
    }

    (sc === window ? window : sc).addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ------------------------------------------------------------------ */
  /* boot                                                                */
  /* ------------------------------------------------------------------ */

  function boot() {
    var onHome = !!document.querySelector(".nx-hero");

    if (reduced) {
      // calm end-state is set by CSS; just wire the counters visible
      initRitual();
      return;
    }

    var sc = scroller();
    initTensionTracking(sc);
    initReveals(document);
    initRitual();
    initGsap(sc);

    var field = onHome && !designMode ? createField() : null;
    var hud = onHome && !designMode ? createHud() : null;

    window.addEventListener(
      "pointermove",
      function (e) {
        pointer.tx = e.clientX / window.innerWidth;
        pointer.ty = e.clientY / window.innerHeight;
      },
      { passive: true }
    );

    var running = true;
    document.addEventListener("visibilitychange", function () {
      running = document.visibilityState === "visible";
      if (running) requestAnimationFrame(tick);
    });

    var root = document.documentElement;
    var lastCss = -1;

    function tick(now) {
      if (!running) return;
      var t = now * 0.001;

      tension.value += (tension.target - tension.value) * 0.06;
      pointer.x += (pointer.tx - pointer.x) * 0.05;
      pointer.y += (pointer.ty - pointer.y) * 0.05;

      // mirror the scalar to CSS no more than ~30x/s and only on change
      var q = Math.round(tension.value * 200);
      if (q !== lastCss) {
        lastCss = q;
        var tv = tension.value;
        root.style.setProperty("--nx-t", tv.toFixed(3));
        root.style.setProperty("--nx-soft", (100 * (1 - tv)).toFixed(1));
        root.style.setProperty("--nx-dwght", (560 - 160 * (1 - tv)).toFixed(0));
      }

      if (field) field.draw(t);
      if (hud) hud.update(t);
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);

    // theme editor: re-run reveals when sections re-render
    document.addEventListener("shopify:section:load", function (e) {
      initReveals(e.target);
      initRitual();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
